import type * as smartWorkspaces from "@eu.mdotm.api/smart-workspaces";
import type { ReportHorizon } from "@eu.mdotm.api/sphere";
import { t } from "i18next";
import { formatDate } from "$root/localization/formatters";

/**
 * Single source of truth for the widget data exports that run through the smart-workspace data
 * provider (`workspaceRepository.exportWorkspaceDataRaw` → `exportDataAsExcel`).
 *
 * Every one of these widgets builds the *same* `DataRequestDTO` shape: mint a fresh uuid per concept,
 * derive `projection` from those uuids, page with a zero offset and a generous limit. The only things
 * that differ per widget are the data provider it targets, the concepts it projects (mirroring that
 * widget's visible columns) and how those concepts are sorted. So the boilerplate lives once in
 * `buildWidgetExportConfig`, and each widget contributes only a small descriptor in `WIDGET_EXPORTS`.
 *
 * `buildWidgetExportConfig(widget, config)` discriminates on the widget key: the `config` it accepts
 * is the one declared for that key in `WidgetExportConfigMap`. This mirrors how
 * `buildCompositionExportConfig` discriminates on entity type in
 * `functional-areas/instruments/export/composition-export.ts`, but keyed by widget instead of entity.
 *
 * Note: PortfolioPerformance is intentionally absent — it exports through a dedicated endpoint
 * (`InvestmentExportControllerApiFactory.exportPerformance`), not the smart-workspace data provider,
 * so it never produces a `DataRequestDTO`. See `PortfolioPerformance/export.ts`.
 */

/** Page size for every widget export — these tables are small; this just lifts the default response limit. */
const WIDGET_EXPORT_LIMIT = 1000;

/** A concept template — a `ParameterizedConceptDTO` minus its `uuid`, which is minted per request. */
type ConceptTemplate = Omit<smartWorkspaces.ParameterizedConceptDTO, "uuid">;

/**
 * A sort the user applied to a widget's table, in the table's native shape — the column's `name` plus
 * an asc/desc direction (structurally shadcn's `OrderBy`). Translated to export `SortCriterionDTO`s
 * per widget by resolving each column to the concept backing it.
 */
export type WidgetExportSort = { columnName: string; direction: "asc" | "desc" };

/**
 * Label for a current-composition column in proposal mode, mirroring the widgets' "Composition {date}"
 * header (`TABLE.HEADERS.COMPOSITION_AT`). The date is the portfolio's modification date with the year
 * omitted — the same construction as `previousWeightLabel` in
 * `functional-areas/instruments/export/composition-export.ts`.
 */
function compositionAtLabel(proposalDate?: string): string {
	return t("TABLE.HEADERS.COMPOSITION_AT", {
		date: proposalDate ? formatDate(proposalDate, { omitYear: true }) : "",
	});
}

// ─── Per-widget config types ────────────────────────────────────────────────────────────────────

/** PortfolioHistoryBlock: the investment whose action-history timeline is exported. */
export type PortfolioHistoryExportConfig = {
	investmentUuid: string;
};

export type PortfolioMonitoringExportConfig = {
	investmentUuid: string;
	/** Which portfolio snapshot to evaluate — `CURRENT` or `PROPOSAL` (the widget's enhanced state). */
	source: smartWorkspaces.SourceEnum;
};

export type AttributionExportConfig = {
	investmentUuid: string;
	benchmarkIdentifier: string;
	/** Mirrors the widget's horizon Select (e.g. "ONE_MONTH"). */
	horizon: ReportHorizon;
};

export type PortfolioMetricsExportConfig = {
	investmentUuid: string;
	/** Which portfolio snapshot to evaluate — `CURRENT` or `PROPOSAL` (the widget's enhanced state). */
	source: smartWorkspaces.SourceEnum;
	/**
	 * The column sort the user applied in the widget table, if any. Empty/absent → the export keeps the
	 * provider's natural metric order; otherwise each entry is mirrored as an export sort criterion.
	 */
	sort?: WidgetExportSort[];
};

export type ExanteMetricsExportConfig = {
	investmentUuid: string;
	benchmarkIdentifier: string;
	/** Which portfolio snapshot to evaluate — `CURRENT` or `PROPOSAL` (the widget's enhanced state). */
	source: smartWorkspaces.SourceEnum;
	/** Proposal modification date used to label the current-composition column ("Composition {date}") in proposal mode. */
	proposalDate?: string;
	/**
	 * The column sort the user applied in the widget table, if any. Empty/absent → the export keeps the
	 * provider's natural metric order; otherwise each entry is mirrored as an export sort criterion.
	 */
	sort?: WidgetExportSort[];
};

/**
 * How the data provider orders the exported factors. `BETA` ranks by the absolute current exposure;
 * `PORTFOLIO_VS_BENCHMARK` ranks by the absolute portfolio-vs-benchmark difference. Mirrors the
 * widget's "OrderBy" toggle.
 */
export type FactorsExposureOrderBy =
	| "BETA"
	| "PORTFOLIO_VS_BENCHMARK"
	| "PORTFOLIO_VS_PROPOSAL"
	| "PROPOSAL_VS_BENCHMARK";

export type FactorsExposureExportConfig = {
	investmentUuid: string;
	benchmarkIdentifier: string;
	/** Mirrors the widget's "Significant values only" toggle. */
	onlySignificant: boolean;
	/**
	 * Mirrors the widget's `enhanced` mode. When true the table — and therefore the export — gains a
	 * Proposal column between Current and Benchmark.
	 */
	enhanced: boolean;
	/** Which portfolio snapshot to evaluate — `CURRENT` or `PROPOSAL` (the widget's enhanced state). */
	source: smartWorkspaces.SourceEnum;
	/** How the data provider orders the exported factors — mirrors the widget's "OrderBy" toggle. */
	orderBy: FactorsExposureOrderBy;
	/** Proposal modification date used to label the current-composition column ("Composition {date}") in enhanced mode. */
	proposalDate?: string;
	/**
	 * The column sort the user applied in the widget table, if any. Empty/absent → the export keeps the
	 * provider's order (the `orderBy` parameter above); otherwise each entry is mirrored as a sort criterion.
	 */
	sort?: WidgetExportSort[];
};

/**
 * Server-side aggregation dimension the `investmentRealizedContribution` provider groups by. Grouped
 * options pair two category dimensions (`<A>_VS_<B>`); `COMPOSITION` is the flat instrument breakdown.
 * Driven by the widget's category Select.
 */
export type ContributionAggregation =
	| "MACRO_VS_MICRO"
	| "MACRO_VS_GEOGRAPHY"
	| "MICRO_VS_GEOGRAPHY"
	| "GEOGRAPHY_VS_MACRO"
	| "GEOGRAPHY_VS_MICRO"
	| "COMPOSITION";

export type ContributionVolatilityExportConfig = {
	investmentUuid: string;
	/** Mirrors the widget's horizon Select (e.g. "ONE_MONTH"). */
	horizon: ReportHorizon;
	/** Aggregation dimension the provider groups by — driven by the widget's category Select. */
	aggregation: ContributionAggregation;
	/** Mirrors the widget's "Significant values only" toggle. */
	onlySignificant: boolean;
	/**
	 * The column sort the user applied in the widget table, if any. Empty/absent → the provider's natural
	 * order; otherwise each entry is mirrored as an export sort criterion.
	 */
	sort?: WidgetExportSort[];
};

export type VolatilityContributionExportConfig = {
	investmentUuid: string;
	horizon: ReportHorizon;
	/** Widget category selectors; combined into the data provider's `aggregation` enum. */
	selector: string;
	vsSelector: string;
	/** Mirrors the widget's "Significant values only" toggle. */
	onlySignificant: boolean;
	/** Which portfolio snapshot to evaluate — `CURRENT` or `PROPOSAL` (the widget's enhanced state). */
	source: smartWorkspaces.SourceEnum;
	/** Proposal modification date used to label the current-composition column ("Composition {date}") in proposal mode. */
	proposalDate?: string;
	/**
	 * The column sort the user applied in the widget table, if any. Empty/absent → the provider's natural
	 * order; otherwise each entry is mirrored as an export sort criterion. The column→concept mapping is
	 * `source`-dependent (the table's columns differ between current and proposal mode).
	 */
	sort?: WidgetExportSort[];
};

/** Maps each widget key to the config its export requires. The discriminant of `buildWidgetExportConfig`. */
export interface WidgetExportConfigMap {
	PortfolioHistoryBlock: PortfolioHistoryExportConfig;
	PortfolioMonitoringBlock: PortfolioMonitoringExportConfig;
	Attribution: AttributionExportConfig;
	PerformanceMetricsBlock: PortfolioMetricsExportConfig;
	ExanteMetricsBlock: ExanteMetricsExportConfig;
	PortfolioFactorsExposure: FactorsExposureExportConfig;
	ContributionVolatility: ContributionVolatilityExportConfig;
	ExanteContributionVolatility: VolatilityContributionExportConfig;
}

/** The set of widget keys that export through the smart-workspace data provider. */
export type WidgetExportKey = keyof WidgetExportConfigMap;

// ─── Concept templates (mirroring each widget's visible columns) ─────────────────────────────────

/**
 * History action concepts mirroring the PortfolioHistoryBlock timeline rows: the event date, the
 * author who performed it and the action type. The first entry (Date) is the sort key.
 */
const HISTORY_ACTION_CONCEPTS: ConceptTemplate[] = [
	{
		conceptId: "systemId: historyAction:date",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Date",
	},
	{
		conceptId: "systemId: historyAction:user",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "User",
	},
	{
		conceptId: "systemId: historyAction:action",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Action",
	},
];

/**
 * Monitoring concepts mirroring the PortfolioMonitoringBlock constraint rows: the constraint type and
 * subject, whether it is a soft constraint, the operator, its threshold values, the actual value and
 * whether it is breached. The first entry (Constraint Type) is the sort key.
 */
const MONITORING_CONCEPTS: ConceptTemplate[] = [
	{
		conceptId: "systemId: monitoring:constraintType",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Constraint Type",
	},
	{
		conceptId: "systemId: monitoring:subject",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Subject",
	},
	{
		conceptId: "systemId: monitoring:soft",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Soft",
	},
	{
		conceptId: "systemId: monitoring:operator",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Operator",
	},
	{
		conceptId: "systemId: monitoring:value1",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Value 1",
	},
	{
		conceptId: "systemId: monitoring:value2",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Value 2",
	},
	{
		conceptId: "systemId: monitoring:actualValue",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Actual Value",
	},
	{
		conceptId: "systemId: monitoring:breached",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Breached",
	},
];

/**
 * Attribution concepts mirroring the Attribution waterfall: the asset class, the attribution type
 * (allocation / selection / interaction effect) and its value.
 */
const ATTRIBUTION_CONCEPTS: ConceptTemplate[] = [
	{
		conceptId: "systemId: performanceAttribution:assetClass",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Asset Class",
	},
	{
		conceptId: "systemId: performanceAttribution:type",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Type",
	},
	{
		conceptId: "systemId: performanceAttribution:value",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Value",
	},
];

/**
 * Portfolio metric concepts mirroring the PortfolioMetrics table columns: the metric name plus its
 * Current and Benchmark values. Keyed by concept role so columns can be resolved by name (never by
 * positional index); the projection order is the insertion order (`Object.values`).
 */
const PORTFOLIO_METRICS_CONCEPTS = {
	metric: {
		conceptId: "systemId: metric:metric",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Metrics",
	},
	current: {
		conceptId: "systemId: metric:current",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Current",
	},
	benchmark: {
		conceptId: "systemId: metric:benchmark",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Benchmark",
	},
} satisfies Record<string, ConceptTemplate>;

/**
 * Maps the PerformanceMetricsBlock table's column `name` (see `PortfolioMetricsBaseTable`) to the
 * `conceptId` of the export concept backing that column, so the user's active table sort can be
 * resolved to the right concept. The table's Proposal column has no projected concept here, so a sort
 * on it is dropped by `tableSortToCriteria`.
 */
const PORTFOLIO_METRICS_COLUMN_CONCEPT_ID_BY_NAME: Record<string, string> = {
	label: PORTFOLIO_METRICS_CONCEPTS.metric.conceptId,
	current: PORTFOLIO_METRICS_CONCEPTS.current.conceptId,
	benchmark: PORTFOLIO_METRICS_CONCEPTS.benchmark.conceptId,
};

/**
 * Ex-ante metric concepts mirroring the ExanteMetricsBlock table columns: the metric name plus its
 * Current and Benchmark values. Keyed by concept role so columns can be resolved by name (never by
 * positional index). The `name` entry is the sort key. In proposal mode the table also shows the
 * proposal snapshot, so a Proposal column is inserted between Current and Benchmark.
 */
const EXANTE_METRIC_CONCEPTS = {
	name: {
		conceptId: "systemId: exAnteMetric:metric",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Metrics",
	},
	current: {
		conceptId: "systemId: exAnteMetric:current",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Current",
	},
	/** Proposal value column — projected only in proposal mode, between Current and Benchmark. */
	proposal: {
		conceptId: "systemId: exAnteMetric:proposal",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Proposal",
	},
	benchmark: {
		conceptId: "systemId: exAnteMetric:benchmark",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Benchmark",
	},
} satisfies Record<string, ConceptTemplate>;

/**
 * Ex-ante metric concepts: Metrics, Current, (Proposal when proposal mode), Benchmark. In proposal mode
 * the Current column is relabelled "Composition {date}", mirroring the widget header.
 */
function buildExanteMetricsConcepts(source: smartWorkspaces.SourceEnum, proposalDate?: string): ConceptTemplate[] {
	const isProposal = source === "PROPOSAL";
	return [
		EXANTE_METRIC_CONCEPTS.name,
		isProposal
			? { ...EXANTE_METRIC_CONCEPTS.current, label: compositionAtLabel(proposalDate) }
			: EXANTE_METRIC_CONCEPTS.current,
		EXANTE_METRIC_CONCEPTS.benchmark,
		...(isProposal ? [EXANTE_METRIC_CONCEPTS.proposal] : []),
	];
}

/**
 * Maps the ExanteMetricsBlock table's column `name` (see `PortfolioMetricsBaseTable`) to the
 * `conceptId` of the export concept backing that column, so the user's active table sort can be
 * resolved to the right concept regardless of proposal-mode column insertion or relabelling.
 */
const EXANTE_COLUMN_CONCEPT_ID_BY_NAME: Record<string, string> = {
	label: EXANTE_METRIC_CONCEPTS.name.conceptId,
	current: EXANTE_METRIC_CONCEPTS.current.conceptId,
	proposal: EXANTE_METRIC_CONCEPTS.proposal.conceptId,
	benchmark: EXANTE_METRIC_CONCEPTS.benchmark.conceptId,
};

/**
 * Factor-exposure concepts mirroring the PortfolioFactorsExposure table columns: the factor name plus
 * its Current, Proposal and Benchmark values. Keyed by concept role so columns can be resolved by name
 * (never by positional index). The Proposal column is projected only in enhanced mode.
 */
const FACTOR_CONCEPTS = {
	/** Factor name column — also the factor-exposure export's leading column. */
	factor: {
		conceptId: "systemId: factor:factor",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Factor",
	},
	/** Current-portfolio composition column for the factor-exposure export. Always projected. */
	current: {
		conceptId: "systemId: factor:current",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Current",
	},
	/** Proposal-portfolio composition column — projected only in enhanced mode, between Current and Benchmark. */
	proposal: {
		conceptId: "systemId: factor:proposal",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Proposal",
	},
	/** Benchmark value column for the factor-exposure export. */
	benchmark: {
		conceptId: "systemId: factor:benchmark",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Benchmark",
	},
} satisfies Record<string, ConceptTemplate>;

/**
 * Factor-exposure concepts: Factor, Current, (Proposal when enhanced), Benchmark. In enhanced mode the
 * Current column is relabelled "Composition {date}", mirroring the widget header.
 */
function buildFactorsExposureConcepts(enhanced: boolean, proposalDate?: string): ConceptTemplate[] {
	return [
		FACTOR_CONCEPTS.factor,
		enhanced ? { ...FACTOR_CONCEPTS.current, label: compositionAtLabel(proposalDate) } : FACTOR_CONCEPTS.current,
		...(enhanced ? [FACTOR_CONCEPTS.proposal] : []),
		FACTOR_CONCEPTS.benchmark,
	];
}

/**
 * Maps the PortfolioFactorsExposure table's column `name` (see its `columns`) to the `conceptId` of the
 * export concept backing that column. The Graph column has no projected concept, so a sort on it is
 * dropped by `tableSortToCriteria`. Column names are the same in enhanced and non-enhanced mode.
 */
const FACTOR_COLUMN_CONCEPT_ID_BY_NAME: Record<string, string> = {
	label: FACTOR_CONCEPTS.factor.conceptId,
	current: FACTOR_CONCEPTS.current.conceptId,
	proposal: FACTOR_CONCEPTS.proposal.conceptId,
	benchmark: FACTOR_CONCEPTS.benchmark.conceptId,
};

/**
 * Contribution concepts mirroring the ContributionVolatility table: the asset class plus its
 * performance and volatility contribution values. Keyed by concept role so columns can be resolved by
 * name (never by positional index); the projection order is the insertion order (`Object.values`).
 */
const CONTRIBUTION_CONCEPTS = {
	assetClass: {
		conceptId: "systemId: contribution:assetClass",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Asset class",
	},
	performance: {
		conceptId: "systemId: contribution:performance",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Portfolio performance",
	},
	volatility: {
		conceptId: "systemId: contribution:volatility",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Portfolio volatility",
	},
} satisfies Record<string, ConceptTemplate>;

/**
 * Maps the ContributionVolatility table's column `name` (the grouped and composition tables share these
 * names) to the `conceptId` of the export concept backing that column. The graph columns have no
 * projected concept, so a sort on them is dropped by `tableSortToCriteria`.
 */
const CONTRIBUTION_COLUMN_CONCEPT_ID_BY_NAME: Record<string, string> = {
	value: CONTRIBUTION_CONCEPTS.assetClass.conceptId,
	performance: CONTRIBUTION_CONCEPTS.performance.conceptId,
	volatility: CONTRIBUTION_CONCEPTS.volatility.conceptId,
};

/** The dimensions the `investmentRealizedContribution` provider can order its rows by. */
export type ContributionOrderBy = "PERFORMANCE" | "VOLATILITY";

/**
 * Maps a ContributionVolatility table column `name` to the `investmentRealizedContribution` provider's
 * `orderBy` token. Only the performance and volatility metric columns are orderable server-side; the
 * asset-class column (and the graph columns) have no token, so a sort on them emits no order param.
 */
const CONTRIBUTION_ORDER_BY_BY_COLUMN_NAME: Record<string, ContributionOrderBy> = {
	performance: "PERFORMANCE",
	volatility: "VOLATILITY",
};

/**
 * Order params for the `investmentRealizedContribution` provider. The provider orders its computed rows
 * via the dedicated `orderBy`/`orderDirection` parameters and can only order by the performance or
 * volatility metric, so we emit them only when the user sorted one of those columns — paired with that
 * sort's direction. Sorting the asset-class column, or no sort at all, leaves both params off, so the
 * provider falls back to its default weight-descending order.
 */
function contributionOrderParams({ sort }: Pick<ContributionVolatilityExportConfig, "sort">): Record<string, string> {
	const orderedSort = (sort ?? []).find(
		(entry) => CONTRIBUTION_ORDER_BY_BY_COLUMN_NAME[entry.columnName] !== undefined,
	);
	if (!orderedSort) {
		return {};
	}
	return {
		orderBy: CONTRIBUTION_ORDER_BY_BY_COLUMN_NAME[orderedSort.columnName],
		orderDirection: orderedSort.direction === "asc" ? "ASC" : "DESC",
	};
}

/**
 * Volatility-contribution concepts mirroring the ExanteContributionVolatility table: the asset-class
 * label (the grouping key) plus its portfolio-volatility contribution, and — in proposal mode — the
 * proposal snapshot's contribution and its delta vs the current value. Keyed by concept role so columns
 * can be resolved by name (never by positional index). The proposal layout appends `proposal`/`delta`
 * after the current volatility column, mirroring the widget (Asset class → Current → Proposal → Delta).
 */
const VOLATILITY_CONTRIBUTION_CONCEPTS = {
	assetClass: {
		conceptId: "systemId: contribution:assetClass",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Asset class",
	},
	volatility: {
		conceptId: "systemId: contribution:volatility",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Portfolio volatility",
	},
	proposal: {
		conceptId: "systemId: contribution:proposal",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Proposal",
	},
	delta: {
		conceptId: "systemId: contribution:delta",
		representationMode: "ABSOLUTE",
		source: "CURRENT",
		parameters: {},
		label: "Delta",
	},
} satisfies Record<string, ConceptTemplate>;

/**
 * Volatility-contribution concepts. In proposal mode the volatility column is relabelled
 * "Composition {date}" (mirroring the widget header) and the Proposal and Delta columns are appended.
 */
function buildVolatilityContributionConcepts(
	source: smartWorkspaces.SourceEnum,
	proposalDate?: string,
): ConceptTemplate[] {
	if (source !== "PROPOSAL") {
		return [VOLATILITY_CONTRIBUTION_CONCEPTS.assetClass, VOLATILITY_CONTRIBUTION_CONCEPTS.volatility];
	}
	return [
		VOLATILITY_CONTRIBUTION_CONCEPTS.assetClass,
		{ ...VOLATILITY_CONTRIBUTION_CONCEPTS.volatility, label: compositionAtLabel(proposalDate) },
		VOLATILITY_CONTRIBUTION_CONCEPTS.proposal,
		VOLATILITY_CONTRIBUTION_CONCEPTS.delta,
	];
}

/**
 * Maps the ExanteContributionVolatility table's column `name` to the `conceptId` of the export concept
 * backing it. The table's column names are `source`-dependent: in proposal (enhanced) mode the columns
 * are `value`/`current`/`proposal`/`difference`, whereas in current mode the asset-class label column is
 * itself named `current` and the only metric column is `volatility`. The Graph column has no projected
 * concept, so a sort on it is dropped by `tableSortToCriteria`.
 */
function volatilityContributionColumnConceptIdByName(source: smartWorkspaces.SourceEnum): Record<string, string> {
	if (source !== "PROPOSAL") {
		return {
			current: VOLATILITY_CONTRIBUTION_CONCEPTS.assetClass.conceptId,
			volatility: VOLATILITY_CONTRIBUTION_CONCEPTS.volatility.conceptId,
		};
	}
	return {
		value: VOLATILITY_CONTRIBUTION_CONCEPTS.assetClass.conceptId,
		current: VOLATILITY_CONTRIBUTION_CONCEPTS.volatility.conceptId,
		proposal: VOLATILITY_CONTRIBUTION_CONCEPTS.proposal.conceptId,
		difference: VOLATILITY_CONTRIBUTION_CONCEPTS.delta.conceptId,
	};
}

/**
 * Short aggregation tokens the `investmentCompositionVolatilityContribution` provider expects per
 * category dimension. The widget's category selectors map to these and are combined as
 * `<selector>_VS_<vsSelector>` — e.g. `assetClass` vs `microAssetClass` → `MACRO_VS_MICRO`.
 */
const AGGREGATION_TOKEN_BY_SELECTOR: Record<string, string> = {
	assetClass: "MACRO",
	microAssetClass: "MICRO",
	geography: "GEOGRAPHY",
};

/** Maps the widget's category selection (`selector`/`vsSelector`) to the data provider's `aggregation` enum. */
function volatilityContributionAggregationFor({
	selector,
	vsSelector,
}: {
	selector: string;
	vsSelector: string;
}): string {
	return `${AGGREGATION_TOKEN_BY_SELECTOR[selector]}_VS_${AGGREGATION_TOKEN_BY_SELECTOR[vsSelector]}`;
}

/** The single dimension the `investmentCompositionVolatilityContribution` provider can order its rows by. */
export type VolatilityContributionOrderBy = "VOLATILITY" | "PROPOSAL" | "DELTA";

const VOLATILITY_CONTRIBUTION_ORDER_BY_BY_COLUMN_NAME: Record<string, VolatilityContributionOrderBy> = {
	volatility: "VOLATILITY",
	proposal: "PROPOSAL",
	difference: "DELTA",
};

/**
 * Order params for the `investmentCompositionVolatilityContribution` provider. The provider orders its
 * computed rows via the dedicated `orderBy`/`orderDirection` parameters and can only order by the
 * volatility metric, so we emit `orderBy: "VOLATILITY"` — paired with the sort direction — only when the
 * user sorted the volatility column (`"volatility"` in current mode, `"current"` (current composition) in
 * proposal mode). Sorting any other column, or no sort at all, leaves both params off entirely.
 */
function volatilityContributionOrderParams({
	source,
	sort,
}: Pick<VolatilityContributionExportConfig, "source" | "sort">): Record<string, string> {
	const orderedSort = (sort ?? []).find(
		(entry) => VOLATILITY_CONTRIBUTION_ORDER_BY_BY_COLUMN_NAME[entry.columnName] !== undefined,
	);
	if (!orderedSort) {
		return {};
	}
	return {
		orderBy: VOLATILITY_CONTRIBUTION_ORDER_BY_BY_COLUMN_NAME[orderedSort.columnName],
		orderDirection: orderedSort.direction === "asc" ? "ASC" : "DESC",
	};
}

// ─── Widget export registry ──────────────────────────────────────────────────────────────────────

type WidgetExportDescriptor<Config> = {
	/** The smart-workspace data provider this widget's rows come from. */
	dataProviderId: string;
	/** Concept templates mirroring the widget's visible columns; may depend on `config`. */
	concepts: (config: Config) => ConceptTemplate[];
	/** Data provider parameters built from `config`. */
	parameters: (config: Config) => Record<string, string | number | boolean>;
	/**
	 * Sort criteria, resolved against the minted concepts and the widget's `config` (e.g. a user-applied
	 * table sort). Omitting this leaves the `sorting` field off the request entirely; returning `[]` sends
	 * an explicit empty sort (the provider's natural order).
	 */
	sorting?: (concepts: smartWorkspaces.ParameterizedConceptDTO[], config: Config) => smartWorkspaces.SortCriterionDTO[];
};

/** Sort by the first projected concept — mirrors a table sorted on its leading column. */
function sortByFirstConcept(
	direction: smartWorkspaces.SortCriterionDTO["direction"],
): WidgetExportDescriptor<unknown>["sorting"] {
	return (concepts) => [{ direction, parameterizedConceptUuid: concepts[0].uuid }];
}

/**
 * Translates a user-applied table sort into export sort criteria by resolving each sorted column's
 * `name` to the minted uuid of the concept backing it (via `columnConceptIdByName`). Columns with no
 * matching concept are dropped; an empty/absent sort yields `[]` — the provider's natural order.
 */
function tableSortToCriteria(
	sort: WidgetExportSort[] | undefined,
	concepts: smartWorkspaces.ParameterizedConceptDTO[],
	columnConceptIdByName: Record<string, string>,
): smartWorkspaces.SortCriterionDTO[] {
	return (sort ?? []).flatMap((entry) => {
		const conceptId = columnConceptIdByName[entry.columnName];
		const concept = concepts.find((candidate) => candidate.conceptId === conceptId);
		if (!concept) {
			return [];
		}
		return [{ direction: entry.direction === "desc" ? "DESC" : "ASC", parameterizedConceptUuid: concept.uuid! }];
	});
}

const WIDGET_EXPORTS: { [K in WidgetExportKey]: WidgetExportDescriptor<WidgetExportConfigMap[K]> } = {
	PortfolioHistoryBlock: {
		dataProviderId: "investmentHistoryAction",
		concepts: () => HISTORY_ACTION_CONCEPTS,
		parameters: ({ investmentUuid }) => ({ investmentUuid }),
		// Sort by the first concept (Date) descending, mirroring the timeline's most-recent-first ordering.
		sorting: sortByFirstConcept("DESC"),
	},
	PortfolioMonitoringBlock: {
		dataProviderId: "investmentMonitoringMetric",
		concepts: () => MONITORING_CONCEPTS,
		parameters: ({ investmentUuid, source }) => ({ investmentUuid, source }),
		// Sort by the first concept (Constraint Type), mirroring the table's default ordering.
		sorting: sortByFirstConcept("ASC"),
	},
	Attribution: {
		dataProviderId: "investmentRealizedPerformanceAttribution",
		concepts: () => ATTRIBUTION_CONCEPTS,
		parameters: ({ investmentUuid, benchmarkIdentifier, horizon }) => ({
			investmentUuid,
			benchmarkIdentifier,
			horizon,
		}),
		// Sort by the value concept (the third column), ascending.
		// sorting: (concepts) => [{ direction: "ASC", parameterizedConceptUuid: concepts[2].uuid }],
		sorting: () => [],
	},
	PerformanceMetricsBlock: {
		dataProviderId: "investmentPortfolioMetric",
		concepts: () => Object.values(PORTFOLIO_METRICS_CONCEPTS),
		parameters: ({ investmentUuid, source }) => ({ investmentUuid, source }),
		// No sort by default (empty array → the provider's natural metric order). When the user sorts a
		// column in the widget table, mirror that sort by resolving the column to its concept.
		sorting: (concepts, { sort }) => tableSortToCriteria(sort, concepts, PORTFOLIO_METRICS_COLUMN_CONCEPT_ID_BY_NAME),
	},
	ExanteMetricsBlock: {
		dataProviderId: "investmentPortfolioExAnteMetric",
		concepts: ({ source, proposalDate }) => buildExanteMetricsConcepts(source, proposalDate),
		parameters: ({ investmentUuid, benchmarkIdentifier, source }) => ({ investmentUuid, benchmarkIdentifier, source }),
		// No sort by default (empty array → the provider's natural metric order). When the user sorts a
		// column in the widget table, mirror that sort by resolving the column to its concept.
		sorting: (concepts, { sort }) => tableSortToCriteria(sort, concepts, EXANTE_COLUMN_CONCEPT_ID_BY_NAME),
	},
	PortfolioFactorsExposure: {
		dataProviderId: "investmentFactor",
		concepts: ({ enhanced, proposalDate }) => buildFactorsExposureConcepts(enhanced, proposalDate),
		parameters: ({ investmentUuid, benchmarkIdentifier, onlySignificant, source, orderBy }) => ({
			investmentUuid,
			benchmarkIdentifier,
			onlySignificant,
			source,
			orderBy,
		}),
		// By default no explicit sort — the provider orders factors via the `orderBy` parameter. When the
		// user sorts a column in the widget table, mirror that sort by resolving the column to its concept.
		sorting: (concepts, { sort }) => tableSortToCriteria(sort, concepts, FACTOR_COLUMN_CONCEPT_ID_BY_NAME),
	},
	ContributionVolatility: {
		dataProviderId: "investmentRealizedContribution",
		concepts: () => Object.values(CONTRIBUTION_CONCEPTS),
		// `orderBy`/`orderDirection` are added only when the user sorted the performance or volatility column
		// (the dimensions the provider can order by); sorting the asset-class column or leaving it unsorted
		// leaves both off, so the provider falls back to its default weight-descending order — see
		// `contributionOrderParams`.
		parameters: ({ investmentUuid, horizon, aggregation, onlySignificant, sort }) => ({
			investmentUuid,
			horizon,
			aggregation,
			onlySignificant,
			...contributionOrderParams({ sort }),
		}),
		// No sort by default (empty array → the provider's natural order). When the user sorts a column in
		// the widget table, mirror that sort by resolving the column to its concept.
		// sorting: (concepts, { sort }) => tableSortToCriteria(sort, concepts, CONTRIBUTION_COLUMN_CONCEPT_ID_BY_NAME),
		sorting: () => [],
	},
	ExanteContributionVolatility: {
		dataProviderId: "investmentCompositionVolatilityContribution",
		concepts: ({ source, proposalDate }) => buildVolatilityContributionConcepts(source, proposalDate),
		// `orderBy`/`orderDirection` are added only when the user sorted the volatility column (the sole
		// dimension the provider can order by); an unsorted table leaves both off — see
		// `volatilityContributionOrderParams`.
		parameters: ({ investmentUuid, horizon, selector, vsSelector, onlySignificant, source, sort }) => ({
			investmentUuid,
			horizon,
			aggregation: volatilityContributionAggregationFor({ selector, vsSelector }),
			onlySignificant,
			source,
			...volatilityContributionOrderParams({ source, sort }),
		}),
		// No sort by default (empty array → the provider's natural order). When the user sorts a column in
		// the widget table, mirror that sort — the column→concept map is `source`-dependent (the table's
		// columns differ between current and proposal mode).
		// sorting: (concepts, { sort, source }) =>
		// 	tableSortToCriteria(sort, concepts, volatilityContributionColumnConceptIdByName(source)),
		sorting: () => [],
	},
};

// ─── Builder ──────────────────────────────────────────────────────────────────────────────────────

/** Cast a plain params record to the data provider's loosely-typed (`{ [key]: object }`) parameter bag. */
function toProviderParameters(params: Record<string, string | number | boolean>): { [key: string]: object } {
	return Object.fromEntries(Object.entries(params).map(([key, value]) => [key, value as unknown as object]));
}

/**
 * Builds the data request used to export a widget's data through the smart-workspace data provider
 * (`workspaceRepository.exportWorkspaceDataRaw` → `exportDataAsExcel`). `widget` discriminates the
 * config required (see `WidgetExportConfigMap`) and selects the data provider, concepts and sort the
 * export should mirror. The exported concepts mirror the widget's visible columns; concept UUIDs are
 * minted fresh per call with `crypto.randomUUID()` and threaded through `projection`/`sorting` so the
 * request stays internally consistent without relying on hard-coded ids.
 *
 * `opts.asOf` pins the export to a past date, carried as the provider's own `asOfDate` parameter and
 * left off entirely in the live view. It is required but nullable: a surface that cannot be pinned
 * passes `undefined`, so adding an export states its stance on the date rather than inheriting one.
 */
export function buildWidgetExportConfig<K extends WidgetExportKey>(
	widget: K,
	config: WidgetExportConfigMap[K],
	opts: { asOf: string | undefined },
): smartWorkspaces.DataRequestDTO {
	const descriptor = WIDGET_EXPORTS[widget] as WidgetExportDescriptor<WidgetExportConfigMap[K]>;

	// Mint a fresh uuid per concept on each call; `projection` and `sorting` reference these uuids, so
	// the request stays internally consistent without relying on hard-coded ids.
	const concepts: smartWorkspaces.ParameterizedConceptDTO[] = descriptor.concepts(config).map((concept) => ({
		uuid: crypto.randomUUID(),
		...concept,
	}));

	const sorting = descriptor.sorting?.(concepts, config);

	return {
		dataProvider: {
			dataProviderId: descriptor.dataProviderId,
			parameters: toProviderParameters({
				...descriptor.parameters(config),
				...(opts.asOf != null ? { asOfDate: opts.asOf } : {}),
			}),
		},
		concepts,
		projection: concepts.map((concept) => concept.uuid!),
		...(sorting !== undefined ? { sorting } : {}),
		offset: 0,
		limit: WIDGET_EXPORT_LIMIT,
	};
}
